import { inject } from '@angular/core';
import { ResolveFn } from '@angular/router';

import { of } from 'rxjs';
import { filter, first, map, switchMap, tap } from 'rxjs/operators';

import { Store } from '@ngrx/store';

import { environment } from '@rocksoft/environment';
import { CategoryType, EnvironmentFeatures } from '@rocksoft/shared';
import { CategoryStoreActions, CategoryStoreSelectors } from '../category';
import { LocationsRouterStoreSelectors } from '../router/index';

export const categoryResolver: (categoryType: CategoryType) => ResolveFn<boolean> = (categoryType: CategoryType) => {
  return () => {
    const store = inject(Store);
    const features: EnvironmentFeatures = environment.features;

    // Check feature flags for categories that require them
    const featureEnabled = (() => {
      switch (categoryType) {
        case 'Bar':
          return features.bar?.enabled ?? true;
        case 'FrontDesk':
          return features.frontdesk?.enabled ?? true;
        case 'Housekeeping':
          return features.housekeeping?.enabled ?? true;
        case 'Kiosk':
          return features.kiosk?.enabled ?? true;
        case 'Staff':
          return features.staff?.enabled ?? true;
        case 'Location':
        case 'Room':
          return true; // No feature flag check for Location and Room
        default:
          return true;
      }
    })();

    if (!featureEnabled) {
      return of(true);
    }

    const requestAndWait = (parentCategoryId?: string) => {
      const requestStatus = CategoryStoreSelectors.selectCategoryRequestStatus(categoryType, parentCategoryId);
      return store.select(requestStatus).pipe(
        first(),
        tap((status) => {
          if (!status) {
            store.dispatch(
              CategoryStoreActions.CategoryResolverActions.categoriesRequested({ categoryType, parentCategoryId })
            );
          }
        }),
        switchMap(() => store.select(requestStatus)),
        filter((status) => status === 'settled'),
        first(),
        map(() => true)
      );
    };

    if (categoryType === 'Brand') {
      return requestAndWait();
    }

    if (categoryType === 'Location') {
      return store.select(LocationsRouterStoreSelectors.selectBrandId).pipe(
        first(),
        switchMap((brandCategoryId) => requestAndWait(brandCategoryId))
      );
    }

    return store.select(LocationsRouterStoreSelectors.selectLocationId).pipe(
      filter((locationCategoryId) => locationCategoryId !== undefined),
      first(),
      switchMap((locationCategoryId) => requestAndWait(locationCategoryId))
    );
  };
};
